/**
 * Combined mega menu (snippets/mega-menu-combined.liquid).
 *
 * Opening, closing and the mobile drill-down are handled by MainMenu (main.js). This component adds:
 *  - Rail switching with hover intent and "menu aim": moving the mouse diagonally from a rail
 *    link towards its panel does not switch to the rail links passed on the way.
 *  - Panel content (dynamic tiles, featured products and promo), loaded with the Section Rendering
 *    API the first time a panel is shown, and cached in sessionStorage for the visit.
 *  - Arrow-key navigation in the rail.
 *  - A 'menu_click' dataLayer event and an 'on:mega-menu:click' DOM event for analytics.
 */
if (!customElements.get('mega-menu-combined')) {
  const SECTION_ID = 'mega-menu-featured';
  const CACHE_PREFIX = 'cc-mega-panel-v2:';
  const CACHE_TTL = 10 * 60 * 1000;
  const AIM_DELAY = 300;
  const AIM_TOLERANCE = 75;

  class MegaMenuCombined extends HTMLElement {
    connectedCallback() {
      this.disclosure = this.closest('.main-nav__item--dropdown');
      this.rail = this.querySelector(':scope > .child-nav');
      this.railItems = [...this.querySelectorAll('.js-combined-rail')];
      this.isRtl = document.documentElement.dir === 'rtl';
      this.mouseLocs = [];

      this.mouseMoveHandler = this.handleMouseMove.bind(this);
      this.resizeHandler = this.updateHeight.bind(this);

      // The panel box scrolls when the rail is taller than the screen (e.g. many brands). Keep the
      // active item's panel in view while scrolling, instead of leaving it at the top.
      this.scroller = this.closest('.main-nav__child');
      this.scrollHandler = this.updateHeight.bind(this);
      if (this.scroller && this.railItems.length) {
        this.scroller.addEventListener('scroll', this.scrollHandler, { passive: true });
      }

      this.railItems.forEach((item) => {
        // Mobile: load the panel when its accordion is opened.
        item.querySelector(':scope > .main-nav__item-content .js-toggle')?.addEventListener('click', () => {
          if (!MegaMenuCombined.isDesktop()) this.loadPanel(item.querySelector(':scope > .child-nav__panel'));
        });
        item.addEventListener('mouseenter', () => this.possiblyActivate(item));
        item.addEventListener('focusin', () => {
          if (MegaMenuCombined.isDesktop()) this.activate(item);
        });
      });

      if (this.rail) {
        this.rail.addEventListener('mouseleave', () => clearTimeout(this.aimTimer));
        this.rail.addEventListener('keydown', this.handleKeydown.bind(this));
      }

      this.addEventListener('mousemove', this.mouseMoveHandler);
      this.addEventListener('click', this.handleClick.bind(this));
      window.addEventListener('on:debounced-resize', this.resizeHandler);

      // MainMenu opens a panel by adding 'is-open' to the dropdown list item.
      if (this.disclosure) {
        this.openObserver = new MutationObserver(() => {
          if (this.disclosure.classList.contains('is-open')) this.handleOpen();
        });
        this.openObserver.observe(this.disclosure, { attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      clearTimeout(this.aimTimer);
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
      if (this.scroller) this.scroller.removeEventListener('scroll', this.scrollHandler);
      if (this.openObserver) this.openObserver.disconnect();
    }

    static isDesktop() {
      if (window.theme && theme.mediaMatches) return theme.mediaMatches.md;
      return window.matchMedia('(min-width: 769px)').matches;
    }

    /**
     * Runs when the top-level panel opens.
     */
    handleOpen() {
      if (!MegaMenuCombined.isDesktop()) {
        // Mobile: rail panels load when their accordion opens (see connectedCallback).
        if (!this.railItems.length) this.loadPanel(this.querySelector('.mega-combined__flat'));
        return;
      }

      if (this.railItems.length) {
        const active = this.railItems.find((item) => item.classList.contains('is-visible'))
          || this.railItems[0];
        this.activate(active);
      } else {
        const flat = this.querySelector('.mega-combined__flat');
        this.fillFallbackPromo(flat?.querySelector('.mega-combined__featured'));
        this.loadPanel(flat);
      }
    }

    /**
     * Makes a rail item the visible one.
     * @param {Element} item - Rail list item.
     */
    activate(item) {
      clearTimeout(this.aimTimer);
      if (!item) return;

      if (this.activeItem !== item) {
        this.railItems.forEach((railItem) => {
          if (railItem !== item) railItem.classList.remove('is-visible');
        });
        item.classList.add('is-visible');
        this.activeItem = item;
      }

      const panel = item.querySelector(':scope > .child-nav__panel');
      this.fillFallbackPromo(panel?.querySelector('.mega-combined__featured'));
      this.updateHeight();
      this.loadPanel(panel);
    }

    /**
     * Copies the block's fallback promo (one <template> per panel) into an empty promo slot.
     * A collection promo loaded by loadPanel() replaces it.
     * @param {?Element} column - The .mega-combined__featured element.
     */
    fillFallbackPromo(column) {
      const slot = column?.querySelector('[data-mega-promo-slot]');
      const template = this.querySelector(':scope > template[data-mega-promo-template]');
      if (slot && template && !slot.childElementCount) slot.append(template.content.cloneNode(true));
    }

    /**
     * Activates a rail item now, or after a delay if the mouse is heading towards the open panel.
     * @param {Element} item - Rail list item.
     */
    possiblyActivate(item) {
      if (!MegaMenuCombined.isDesktop()) return;
      clearTimeout(this.aimTimer);

      const delay = this.activationDelay();
      if (delay) {
        this.aimTimer = setTimeout(() => this.possiblyActivate(item), delay);
      } else {
        this.activate(item);
      }
    }

    handleMouseMove(evt) {
      this.mouseLocs.push({ x: evt.clientX, y: evt.clientY });
      if (this.mouseLocs.length > 3) this.mouseLocs.shift();
    }

    /**
     * Returns a delay (ms) if the mouse is moving towards the active panel, otherwise 0.
     * Based on the "menu aim" technique: compare the slopes from the current and previous mouse
     * position to the panel's two near corners.
     * @returns {number}
     */
    activationDelay() {
      const panel = this.activeItem?.querySelector(':scope > .child-nav__panel');
      if (!panel) return 0;

      const loc = this.mouseLocs[this.mouseLocs.length - 1];
      const prevLoc = this.mouseLocs[0];
      if (!loc || !prevLoc) return 0;

      const rect = panel.getBoundingClientRect();
      const edgeX = this.isRtl ? rect.right : rect.left;

      // Mouse is already over the panel side.
      if ((!this.isRtl && loc.x > edgeX) || (this.isRtl && loc.x < edgeX)) return 0;

      // Mouse has not moved since the last delay; stop waiting.
      if (this.lastDelayLoc && loc.x === this.lastDelayLoc.x && loc.y === this.lastDelayLoc.y) return 0;

      const upper = { x: edgeX, y: rect.top - AIM_TOLERANCE };
      const lower = { x: edgeX, y: rect.bottom + AIM_TOLERANCE };
      const slope = (a, b) => (b.y - a.y) / (b.x - a.x || 0.0001);

      const upperSlope = slope(loc, upper);
      const lowerSlope = slope(loc, lower);
      const prevUpperSlope = slope(prevLoc, upper);
      const prevLowerSlope = slope(prevLoc, lower);

      const movingTowards = this.isRtl
        ? upperSlope > prevUpperSlope && lowerSlope < prevLowerSlope
        : upperSlope < prevUpperSlope && lowerSlope > prevLowerSlope;

      if (movingTowards) {
        this.lastDelayLoc = loc;
        return AIM_DELAY;
      }

      this.lastDelayLoc = null;
      return 0;
    }

    /**
     * Positions the active panel and keeps the rail tall enough for it (the panel is absolutely
     * positioned). When the panel box is scrolled, the panel moves down with the visible area, but
     * never past the end of the rail.
     */
    updateHeight() {
      if (!this.rail || !this.activeItem || !MegaMenuCombined.isDesktop()) return;
      const panel = this.activeItem.querySelector(':scope > .child-nav__panel');
      if (!panel) return;

      const railItems = [...this.rail.children].filter((li) => li.offsetParent !== null);
      const railHeight = railItems.reduce((sum, li) => sum + li.offsetHeight, 0);

      // The panel is never taller than the visible part of the box (its content scrolls inside),
      // so it can always be shown in full next to the rail.
      let panelMax = Infinity;
      if (this.scroller) {
        const style = getComputedStyle(this.scroller);
        panelMax = this.scroller.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        this.rail.style.setProperty('--panel-max-height', `${Math.max(200, Math.floor(panelMax))}px`);
      }
      const panelHeight = Math.min(panel.scrollHeight, panelMax);

      let panelTop = 0;
      if (this.scroller) {
        const railTop = this.rail.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top
          + this.scroller.scrollTop;
        const maxTop = Math.max(0, railHeight - panelHeight);
        panelTop = Math.min(Math.max(0, this.scroller.scrollTop - railTop), maxTop);
      }

      this.rail.style.setProperty('--panel-top', `${Math.round(panelTop)}px`);
      this.rail.style.setProperty('--sidebar-height', `${Math.max(railHeight, panelTop + panelHeight)}px`);
    }

    /**
     * Arrow keys move between rail links; ArrowRight (ArrowLeft in RTL) moves into the panel.
     * @param {KeyboardEvent} evt - Event object.
     */
    handleKeydown(evt) {
      if (!MegaMenuCombined.isDesktop()) return;

      const railLink = evt.target.closest('.child-nav > li > .main-nav__item-content > .child-nav__item, .child-nav > li > .child-nav__item');
      if (!railLink) return;

      const railLinks = [...this.rail.querySelectorAll(':scope > li > .main-nav__item-content > .child-nav__item, :scope > li > .child-nav__item')]
        .filter((link) => link.offsetParent !== null);
      const index = railLinks.indexOf(railLink);
      const intoPanelKey = this.isRtl ? 'ArrowLeft' : 'ArrowRight';

      if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
        evt.preventDefault();
        const step = evt.key === 'ArrowDown' ? 1 : -1;
        const next = railLinks[(index + step + railLinks.length) % railLinks.length];
        next?.focus();
      } else if (evt.key === intoPanelKey) {
        const panelLink = railLink.closest('li')?.querySelector('.child-nav__panel a');
        if (panelLink) {
          evt.preventDefault();
          panelLink.focus();
        }
      }
    }

    /**
     * Sends menu clicks to the dataLayer (GA4/GTM) and as a DOM event.
     * @param {MouseEvent} evt - Event object.
     */
    handleClick(evt) {
      const link = evt.target.closest('a[data-menu-level]');
      if (!link) return;

      const detail = {
        menuParent: this.dataset.parentTitle,
        menuLevel: link.dataset.menuLevel,
        menuLabel: link.textContent.trim().replace(/\s+/g, ' '),
        menuUrl: link.getAttribute('href')
      };

      document.dispatchEvent(new CustomEvent('on:mega-menu:click', { detail }));

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'menu_click',
          menu_parent: detail.menuParent,
          menu_level: detail.menuLevel,
          menu_label: detail.menuLabel,
          menu_url: detail.menuUrl
        });
      }
    }

    /**
     * Loads a panel's content once: tiles (dynamic mode), featured products and promo, all from
     * one Section Rendering API request for the panel's collection.
     * @param {?Element} panel - The .child-nav__panel (rail) or .mega-combined__flat element.
     */
    async loadPanel(panel) {
      const column = panel?.querySelector('.mega-combined__featured');
      const source = panel?.dataset.megaPanelUrl || column?.dataset.megaFeaturedUrl;
      if (!panel || panel.dataset.state || !source) return;
      panel.dataset.state = 'loading';
      if (column) column.dataset.state = 'loading';

      const url = new URL(source, window.location.origin);
      url.searchParams.set('section_id', SECTION_ID);
      const cacheKey = `${CACHE_PREFIX}${url.pathname}${url.search}`;

      try {
        let html = MegaMenuCombined.readCache(cacheKey);
        if (!html) {
          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          html = await response.text();
          MegaMenuCombined.writeCache(cacheKey, html);
        }

        this.renderPanel(panel, column, html);
        panel.dataset.state = 'loaded';
        if (column) column.dataset.state = 'loaded';
      } catch (error) {
        // Allow a new attempt the next time the panel is shown.
        delete panel.dataset.state;
        if (column) delete column.dataset.state;
        console.log(error); // eslint-disable-line
      }

      this.updateHeight();
    }

    /**
     * Inserts fetched tiles, products and promo into a panel.
     * @param {Element} panel - The panel element.
     * @param {?Element} column - The panel's .mega-combined__featured element.
     * @param {string} html - Section Rendering API response.
     */
    renderPanel(panel, column, html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const tilesSlot = panel.querySelector('[data-mega-tiles-slot]');
      const tiles = doc.querySelector('[data-mega-tiles]');
      if (tilesSlot && tiles) {
        // Filter links are built from the section request URL; drop the section_id parameter.
        tiles.querySelectorAll('a[href]').forEach((link) => {
          const href = new URL(link.getAttribute('href'), window.location.origin);
          href.searchParams.delete('section_id');
          link.setAttribute('href', `${href.pathname}${href.search}`);
        });
        tilesSlot.replaceChildren(...tiles.children);
        tilesSlot.dataset.source = tiles.dataset.megaTilesSource || '';
      }

      if (!column) return;
      const count = Number.parseInt(this.dataset.featuredCount, 10) || 0;
      const productsWrapper = column.querySelector('[data-mega-products-wrapper]');
      const productsSlot = column.querySelector('[data-mega-products-slot]');
      const cards = [...doc.querySelectorAll('[data-mega-products] > *')].slice(0, count);
      if (productsSlot && cards.length) {
        productsSlot.replaceChildren(...cards);
        productsWrapper.hidden = false;
      }

      const promo = doc.querySelector('[data-mega-promo]');
      const promoSlot = column.querySelector('[data-mega-promo-slot]');
      if (promo && promoSlot) promoSlot.replaceChildren(...promo.children);
    }

    static readCache(key) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(key));
        if (cached && Date.now() - cached.time < CACHE_TTL) return cached.html;
      } catch (e) {
        // Storage unavailable or invalid entry; fetch instead.
      }
      return null;
    }

    static writeCache(key, html) {
      try {
        sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), html }));
      } catch (e) {
        // Storage full or unavailable; skip caching.
      }
    }
  }

  customElements.define('mega-menu-combined', MegaMenuCombined);
}
